import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, RevealStagger, RevealItem } from "../components/Reveal";

const About = () => {
  const { t } = useLang();
  const manifesto = t("about.manifesto");
  const facts = t("about.facts");
  const values = t("about.values");

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-24 px-5 md:px-10">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="tag">{t("about.kicker")}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="h-display mt-6 md:mt-8" data-testid="about-title">
                {t("about.title_a")}
                <span className="block ml-[3vw] md:ml-[5vw] text-[#E5FF00]">
                  {t("about.title_b")}
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-zinc-400 text-base md:text-lg leading-relaxed mt-7 md:mt-10 max-w-2xl">
                {t("about.lede")}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-4 lg:col-start-9 self-end">
            <Reveal delay={0.3}>
              <div className="aspect-[4/5] overflow-hidden relative border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1760598748022-ad3478e68903?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                  alt="Prague at night"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
                <div className="absolute top-4 left-4 right-4 flex justify-between font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/80">
                  <span>50.0875° N</span>
                  <span>14.4213° E</span>
                </div>
                <div className="absolute bottom-4 left-4 font-display text-3xl text-[#E5FF00]">
                  Praha
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="px-5 md:px-10 py-16 md:py-32 border-t border-white/10" data-testid="about-manifesto">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <div className="lg:col-span-3">
            <Reveal>
              <span className="tag">{t("about.manifesto_title")}</span>
            </Reveal>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            <RevealStagger className="space-y-6 md:space-y-12">
              {manifesto.map((line, i) => (
                <RevealItem key={i}>
                  <div className="flex items-start gap-4 md:gap-6 border-b border-white/10 pb-6 md:pb-12">
                    <span className="font-mono-tech text-[10px] md:text-xs text-[#E5FF00] uppercase tracking-[0.2em] mt-1 md:mt-2 shrink-0">
                      0{i + 1}
                    </span>
                    <p className="font-display text-xl sm:text-2xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                      {line}
                    </p>
                  </div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-5 md:px-10 py-16 md:py-32 border-t border-white/10" data-testid="about-values">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-10 md:mb-14">
            <div className="lg:col-span-3">
              <Reveal>
                <span className="tag">{t("about.values_kicker")}</span>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal delay={0.1}>
                <h2 className="h-display-md">{t("about.values_title")}</h2>
              </Reveal>
            </div>
          </div>

          <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {values.map((v, i) => (
              <RevealItem
                key={i}
                className="bg-[#0a0a0a] p-7 md:p-14 hover:bg-[#0e0e0e] transition-colors group"
              >
                <div className="flex items-start justify-between mb-7 md:mb-10">
                  <span className="font-mono-tech text-xs text-[#E5FF00] uppercase tracking-[0.2em]">
                    0{i + 1} /
                  </span>
                  <ArrowUpRight
                    size={24}
                    className="text-zinc-700 group-hover:text-[#E5FF00] group-hover:rotate-45 transition-all duration-300"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-display text-2xl md:text-4xl tracking-tight mb-4 md:mb-5">
                  {v.t}
                </h3>
                <p className="text-zinc-400 leading-relaxed text-[15px] md:text-base">{v.d}</p>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Facts */}
      <section className="px-5 md:px-10 py-16 md:py-32 border-t border-white/10 bg-[#080808]" data-testid="about-facts">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-10 md:mb-14">
            <div className="lg:col-span-3">
              <Reveal>
                <span className="tag">{t("about.facts_kicker")}</span>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal delay={0.1}>
                <h2 className="h-display-md">Terwilliger s.r.o.</h2>
              </Reveal>
            </div>
          </div>

          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {facts.map((f, i) => (
              <RevealItem key={i} className="bg-[#050505] p-6 md:p-10">
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                  {f.k}
                </p>
                <p className="font-display text-lg md:text-2xl mt-3 md:mt-4 tracking-tight break-words">
                  {f.v}
                </p>
              </RevealItem>
            ))}
          </RevealStagger>

          <Reveal delay={0.2}>
            <div className="mt-12 md:mt-16">
              <Link to="/contact" className="btn-acid" data-testid="about-cta">
                {t("nav.cta")}
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
